import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observer } from 'rxjs';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {

  constructor(
    private stateService: StateService,
    private router: Router,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  notify(severity: 'success' | 'info' | 'warn' | 'error', detail: string, summary?: string): void {
    this.messageService.add({
      severity,
      summary: summary ?? this.translate.instant(`common.${severity}.title`),
      detail,
      life: severity === 'error' ? 6000 : 4000,
    });
  }

  /**
   * Observer factory for entity create/edit/delete mutations: shows a
   * translated success toast and runs the caller's reload/navigation
   * callback on completion. `error` is a deliberate no-op — every
   * `libs/angular/api` service already calls `handleError` in its own
   * `catchError` before rethrowing, so handling it again here would
   * double the error toast.
   */
  handleMutation<T>(opts: {
    successMessageKey: string;
    onSuccess?: (result: T) => void;
  }): Partial<Observer<T>> {
    return {
      next: () => this.notify('success', this.translate.instant(opts.successMessageKey)),
      error: () => undefined,
      complete: () => opts.onSuccess?.(undefined as T),
    };
  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof Blob) {
      throw error;
    }

    if (error.status === 0) {
      console.error('An error occurred:', error.error);
      this.notify('error', this.translate.instant('error.network'));
      return [];
    } else if (error.status === 401) {
      // Session expired — purge client state and redirect to login.
      this.stateService.setCurrentUser(null);
      const returnUrl = this.router.url;
      this.router.navigate(['/login'], { queryParams: { returnUrl } });
      return [];
    } else if (error.status === 403) {
      this.notify('error', this.translate.instant('error.forbidden'));
      return [];
    } else if (error.status === 406) {
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
      // Some 406 responses carry a structured payload nested inside
      // `message` — a translation key plus interpolation params (e.g.
      // StagesController's delete-guard, reporting how many child
      // stages/measurements are blocking removal) — rather than a flat
      // string. It has to be nested there specifically: the global
      // PostHogExceptionFilter rebuilds every error response and only ever
      // preserves the `message` field, discarding any sibling properties on
      // the thrown exception's response object.
      const nestedMessage = error.error?.message;
      if (nestedMessage && typeof nestedMessage === 'object' && typeof nestedMessage.key === 'string') {
        const { key, tree, ...params } = nestedMessage;
        this.notify('error', this.translate.instant(key, params));
      } else {
        this.notify('error', this.translate.instant('error.not-acceptable'));
      }
      return [];
    } else if (error.status === 413) {
      this.notify('error', this.translate.instant('error.fileTooLarge'));
      return [];
    } else if (error.status >= 500) {
      this.notify('error', this.translate.instant('error.server'));
      return [];
    } else if (error.status >= 400) {
      const detail = error.error?.message ?? error.message ?? this.translate.instant('error.generic');
      this.notify('error', detail);
      return [];
    }

    return [];
  }

}
